
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Shield } from 'lucide-react'
import { useCreateLicense } from '@/hooks'
import { useUIStore } from '@/stores/ui-store'
import type { PlanTier } from '@/lib/types'
import type { CreateLicenseFormData } from '../types'
import { ErrorModal } from './ErrorModal'

export function CreateLicenseModal() {
  const { modalOpen, closeModal } = useUIStore()
  const { mutate: createLicense, isPending } = useCreateLicense()

  const isOpen = modalOpen.type === 'create-license'

  const [formData, setFormData] = useState<CreateLicenseFormData>({
    plan: 'starter',
    expirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    features: ['api-access'],
    maxTenants: 10,
    maxSubTenants: 0,
    maxExtensions: 10,
    enforcementMode: 'warn',
  })

  const [validationError, setValidationError] = useState<any>(null)

  if (!isOpen) return null

  const handleSubmit = () => {
    // Transform features array (UI) to features object (Backend Schema)
    const featuresObject = {
      whatsapp: formData.features.includes('whatsapp'),
      aiAssistant: formData.features.includes('api-access'), // Mapping: api-access -> aiAssistant
      customIntegrations: formData.features.includes('sso'), // Mapping: sso -> customIntegrations
      advancedAnalytics: formData.features.includes('analytics'), // Mapping: analytics -> advancedAnalytics
      prioritySupport: formData.features.includes('white-label'), // Mapping: white-label -> prioritySupport (approx)
    }

    createLicense(
      {
        plan: formData.plan,
        expirationDate: new Date(formData.expirationDate).toISOString(),
        maxTenants: formData.maxTenants,
        maxSubTenants: formData.maxSubTenants,
        maxExtensions: formData.maxExtensions,
        enforcementMode: formData.enforcementMode,
        features: featuresObject,
      },
      {
        onSuccess: () => {
          closeModal()
          setValidationError(null)
        },
        onError: (err: any) => {
          setValidationError(err)
        }
      }
    )
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
        <Card className="w-full max-w-lg shadow-xl">
          <CardHeader>
            <CardTitle>Create New License</CardTitle>
            <CardDescription>Generate a cryptographically signed license key.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Plan Tier</Label>
                  <Select
                    value={formData.plan}
                    onValueChange={(value) => setFormData({ ...formData, plan: value as PlanTier })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Enforcement</Label>
                  <Select
                    value={formData.enforcementMode}
                    onValueChange={(value) => setFormData({ ...formData, enforcementMode: value as any })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="warn">Warn (Soft Limit)</SelectItem>
                      <SelectItem value="stop">Stop (Hard Limit)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Expiration Date</Label>
                <Input
                  type="date"
                  value={formData.expirationDate}
                  onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Tenants</Label>
                  <Input
                    type="number"
                    min="-1"
                    placeholder="-1 for unlimited"
                    value={formData.maxTenants}
                    onChange={(e) => setFormData({ ...formData, maxTenants: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sub-Tenants</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.maxSubTenants}
                    onChange={(e) => setFormData({ ...formData, maxSubTenants: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Extensions</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.maxExtensions}
                    onChange={(e) => setFormData({ ...formData, maxExtensions: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Features</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-muted/20">
                  {['analytics', 'api-access', 'whatsapp', 'white-label', 'sso'].map(feat => (
                    <div key={feat} className="flex items-center space-x-2 bg-background border px-2 py-1 rounded-sm">
                      <input
                        type="checkbox"
                        id={`feat-${feat}`}
                        className="h-3.5 w-3.5 rounded border-gray-300 accent-primary"
                        checked={formData.features.includes(feat)}
                        onChange={(e) => {
                          if (e.target.checked) setFormData({ ...formData, features: [...formData.features, feat] })
                          else setFormData({ ...formData, features: formData.features.filter(f => f !== feat) })
                        }}
                      />
                      <label htmlFor={`feat-${feat}`} className="text-xs font-medium cursor-pointer select-none capitalize">
                        {feat.replace('-', ' ')}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isPending} className="gap-2">
                <Shield className="h-4 w-4" />
                Create License
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <ErrorModal
        isOpen={!!validationError}
        onClose={() => setValidationError(null)}
        title="Failed to Create License"
        message={validationError?.message || "An unexpected error occurred while creating the license key."}
        details={validationError?.fieldErrors}
      />
    </>
  )
}
